import { type CallFeed } from "matrix-js-sdk/lib/webrtc/callFeed";
import { MatrixCall } from "matrix-js-sdk/lib/webrtc/call";
import { SDPStreamMetadataPurpose } from "matrix-js-sdk/lib/webrtc/callEventTypes";
import { loadDevicePreferences } from "../../features/groupCalls/services/devicePreferences";
interface AudioElementInfo {
    element: HTMLAudioElement;
    stream: MediaStream;
    callId: string;
    type: 'local' | 'remote';
}

export class VoiceHandler {
    private audioElements: Map<string, AudioElementInfo> = new Map();
    private audioContext: AudioContext | null = null;
    private analyserNodes: Map<string, AnalyserNode> = new Map();

    constructor() {
        this.initializeAudioContext();
    }

    /**
     * Initialize Web Audio API context for advanced audio processing
     */
    private initializeAudioContext(): void {
        try {
            this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            console.log('Audio context initialized');
        } catch (error) {
            console.warn('Failed to initialize audio context:', error);
        }
    }

    /**
     * Main method to handle feeds changed event from Matrix call
     */
    handleCallFeedsChanged(callId: string, call: MatrixCall): void {
        console.log(`[VoiceHandler] Handling feeds for call ${callId}`);

        const localFeeds = call.getLocalFeeds();
        const remoteFeeds = call.getRemoteFeeds();

        console.log(`[VoiceHandler] Call ${callId} feeds:`, {
            localFeeds: localFeeds.length,
            remoteFeeds: remoteFeeds.length
        });

        this.processLocalFeeds(callId, localFeeds);

        this.processRemoteFeeds(callId, remoteFeeds);
    }

    /**
     * Process local feeds (your microphone/camera)
     */
    private processLocalFeeds(callId: string, localFeeds: CallFeed[]): void {
        localFeeds.forEach((feed, index) => {
            const stream = feed.stream;

            if (stream) {
                const audioTracks = stream.getAudioTracks();
                const videoTracks = stream.getVideoTracks();

                console.log(`[VoiceHandler] Local feed ${index}:`, {
                    streamId: stream.id,
                    audioTracks: audioTracks.length,
                    videoTracks: videoTracks.length,
                    audioEnabled: audioTracks.some(track => track.enabled && track.readyState === 'live')
                });

                if (audioTracks.length > 0 && feed.purpose !== SDPStreamMetadataPurpose.Screenshare) {
                    this.setupLocalAudio(callId, stream);
                }
            }
        });
    }

    /**
     * Process remote feeds (other person's audio/video) - THIS IS THE VOICE YOU HEAR
     */
    private processRemoteFeeds(callId: string, remoteFeeds: CallFeed[]): void {
        remoteFeeds.forEach((feed, index) => {
            const stream = feed.stream;

            if (stream) {
                const audioTracks = stream.getAudioTracks();
                const videoTracks = stream.getVideoTracks();

                console.log(`[VoiceHandler] Remote feed ${index}:`, {
                    streamId: stream.id,
                    audioTracks: audioTracks.length,
                    videoTracks: videoTracks.length,
                    audioEnabled: audioTracks.some(track => track.enabled && track.readyState === 'live')
                });

                // Set up remote audio (other person's voice) - MOST IMPORTANT
                if (audioTracks.length > 0 && feed.purpose !== SDPStreamMetadataPurpose.Screenshare) {
                    this.setupRemoteAudio(callId, stream);
                }
            }
        });
    }

    /**
     * Set up local audio element (your microphone - usually muted to prevent echo)
     */
    private setupLocalAudio(callId: string, stream: MediaStream): void {
        const elementId = `local-audio-${callId}`;

        // Remove existing element if present
        this.removeAudioElement(elementId);

        // Create new audio element
        const audioElement = document.createElement('audio');
        audioElement.id = elementId;
        audioElement.autoplay = true;
        audioElement.muted = true; // Mute local audio to prevent echo
        audioElement.volume = 0; // Keep volume at 0 for local
        audioElement.style.display = 'none';

        // Set up the stream
        audioElement.srcObject = stream;

        // Add event listeners
        this.addAudioEventListeners(audioElement, 'local', callId);

        // Add to DOM
        document.body.appendChild(audioElement);

        // Store reference
        this.audioElements.set(elementId, {
            element: audioElement,
            stream,
            callId,
            type: 'local'
        });

        console.log(`[VoiceHandler] Local audio set up for call ${callId}`);
    }

    /**
     * Set up remote audio element (other person's voice) - THIS IS WHAT YOU HEAR
     */
    private setupRemoteAudio(callId: string, stream: MediaStream): void {
        const elementId = `remote-audio-${callId}`;

        // Remove existing element if present
        this.removeAudioElement(elementId);

        // Create new audio element
        const audioElement = document.createElement('audio');
        audioElement.id = elementId;
        audioElement.autoplay = true;
        audioElement.muted = false; // DO NOT MUTE - this is what you want to hear
        audioElement.volume = 1.0; // Full volume
        audioElement.style.display = 'none';

        // Set up the stream
        audioElement.srcObject = stream;
        const outputId = loadDevicePreferences().audioOutputId;
        if (outputId && 'setSinkId' in audioElement) {
            void (audioElement as HTMLAudioElement & {setSinkId(id: string): Promise<void>}).setSinkId(outputId).catch(() => undefined);
        }

        // Add event listeners
        this.addAudioEventListeners(audioElement, 'remote', callId);

        // Set up audio analysis if context is available
        if (this.audioContext) {
            this.setupAudioAnalysis(elementId, audioElement);
        }

        // Add to DOM
        document.body.appendChild(audioElement);

        // Store reference
        this.audioElements.set(elementId, {
            element: audioElement,
            stream,
            callId,
            type: 'remote'
        });

        console.log(`[VoiceHandler] Remote audio set up for call ${callId}`);

        // Force play (some browsers require user interaction)
        this.ensureAudioPlaying(audioElement, callId);
    }

    /**
     * Add audio event listeners for debugging and monitoring
     */
    private addAudioEventListeners(audioElement: HTMLAudioElement, type: 'local' | 'remote', callId: string): void {
        audioElement.addEventListener('loadstart', () => {
            console.log(`[VoiceHandler] ${type} audio loadstart for call ${callId}`);
        });

        audioElement.addEventListener('loadedmetadata', () => {
            console.log(`[VoiceHandler] ${type} audio metadata loaded for call ${callId}`);
        });

        audioElement.addEventListener('canplay', () => {
            console.log(`[VoiceHandler] ${type} audio can play for call ${callId}`);
        });

        audioElement.addEventListener('play', () => {
            console.log(`[VoiceHandler] ${type} audio started playing for call ${callId}`);
        });

        audioElement.addEventListener('pause', () => {
            console.log(`[VoiceHandler] ${type} audio paused for call ${callId}`);
        });

        audioElement.addEventListener('error', (error) => {
            console.error(`[VoiceHandler] ${type} audio error for call ${callId}:`, error);
        });

        audioElement.addEventListener('stalled', () => {
            console.warn(`[VoiceHandler] ${type} audio stalled for call ${callId}`);
        });
    }

    /**
     * Set up audio analysis for volume levels
     */
    private setupAudioAnalysis(elementId: string, audioElement: HTMLAudioElement): void {
        if (!this.audioContext) return;

        try {
            const source = this.audioContext.createMediaElementSource(audioElement);
            const analyser = this.audioContext.createAnalyser();

            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.8;

            source.connect(analyser);
            analyser.connect(this.audioContext.destination);

            this.analyserNodes.set(elementId, analyser);

            console.log(`[VoiceHandler] Audio analysis set up for ${elementId}`);
        } catch (error) {
            console.warn(`[VoiceHandler] Failed to set up audio analysis for ${elementId}:`, error);
        }
    }

    /**
     * Ensure audio is playing (some browsers require user interaction)
     */
    private async ensureAudioPlaying(audioElement: HTMLAudioElement, callId: string): Promise<void> {
        try {
            await audioElement.play();
            console.log(`[VoiceHandler] Audio playing for call ${callId}`);
        } catch (error) {
            console.warn(`[VoiceHandler] Audio play failed for call ${callId}, might need user interaction:`, error);

            // Add click listener to document to enable audio on user interaction
            const enableAudio = () => {
                audioElement.play().then(() => {
                    console.log(`[VoiceHandler] Audio enabled after user interaction for call ${callId}`);
                    document.removeEventListener('click', enableAudio);
                }).catch(err => {
                    console.error(`[VoiceHandler] Still failed to enable audio:`, err);
                });
            };

            document.addEventListener('click', enableAudio, { once: true });
        }
    }

    /**
     * Get audio volume level for a call (0.0 to 1.0)
     */
    getAudioLevel(callId: string, type: 'local' | 'remote'): number {
        const elementId = `${type}-audio-${callId}`;
        const analyser = this.analyserNodes.get(elementId);

        if (!analyser) return 0;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);

        // Calculate average volume
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;

        return average / 255; // Normalize to 0-1
    }

    /**
     * Set remote audio volume
     */
    setRemoteVolume(callId: string, volume: number): void {
        const elementId = `remote-audio-${callId}`;
        const audioInfo = this.audioElements.get(elementId);

        if (audioInfo) {
            audioInfo.element.volume = Math.max(0, Math.min(1, volume));
            console.log(`[VoiceHandler] Set remote volume to ${volume} for call ${callId}`);
        }
    }

    /**
     * Check if remote audio is playing
     */
    isRemoteAudioPlaying(callId: string): boolean {
        const elementId = `remote-audio-${callId}`;
        const audioInfo = this.audioElements.get(elementId);

        return audioInfo ? !audioInfo.element.paused : false;
    }

    /**
     * Get audio element for external use (e.g., attaching to UI)
     */
    getAudioElement(callId: string, type: 'local' | 'remote'): HTMLAudioElement | null {
        const elementId = `${type}-audio-${callId}`;
        const audioInfo = this.audioElements.get(elementId);

        return audioInfo ? audioInfo.element : null;
    }

    /**
     * Remove audio element
     */
    private removeAudioElement(elementId: string): void {
        const existingInfo = this.audioElements.get(elementId);
        if (existingInfo) {
            existingInfo.element.pause();
            existingInfo.element.srcObject = null;
            existingInfo.element.remove();
            this.audioElements.delete(elementId);
            this.analyserNodes.delete(elementId);
        }
    }

    /**
     * Clean up all media for a specific call
     */
    cleanupCall(callId: string): void {
        console.log(`[VoiceHandler] Cleaning up call ${callId}`);

        // Clean up audio elements
        const audioElementIds = [`local-audio-${callId}`, `remote-audio-${callId}`];
        audioElementIds.forEach(id => this.removeAudioElement(id));

        console.log(`[VoiceHandler] Cleanup complete for call ${callId}`);
    }

    /**
     * Clean up all media elements and audio context
     */
    cleanup(): void {
        console.log('[VoiceHandler] Cleaning up all media');

        // Clean up all audio elements
        this.audioElements.forEach((_, elementId) => {
            this.removeAudioElement(elementId);
        });

        // Close audio context
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
            this.audioContext = null;
        }

        this.analyserNodes.clear();

        console.log('[VoiceHandler] Complete cleanup finished');
    }

    /**
     * Get debug information about current media elements
     */
    getDebugInfo(): any {
        return {
            audioElements: Array.from(this.audioElements.entries()).map(([id, info]) => ({
                id,
                callId: info.callId,
                type: info.type,
                playing: !info.element.paused,
                volume: info.element.volume,
                muted: info.element.muted,
                streamActive: info.stream.active,
                audioTracks: info.stream.getAudioTracks().map(track => ({
                    id: track.id,
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState
                }))
            })),
            audioContext: this.audioContext ? {
                state: this.audioContext.state,
                sampleRate: this.audioContext.sampleRate
            } : null
        };
    }
}